// Browser-compatible asymmetric encryption utilities using Web Crypto API
// Uses RSA public key from backend + AES hybrid encryption

class AsymmetricEncryptionUtils {
    constructor() {
        // Check for Web Crypto API support
        if (!window.crypto || !window.crypto.subtle) {
            console.warn('Web Crypto API not supported. Encryption will be disabled.');
            this.isSupported = false;
            return;
        }
        
        this.isSupported = true;
        this.backendPublicKey = null;
        this.publicKeyFetched = false;
        this.algorithm = 'AES-CBC';
        this.rsaAlgorithm = 'RSA-OAEP';
        
        // Frontend's own key pair for receiving encrypted responses
        this.frontendKeyPair = null;
        this.frontendPublicKeyPem = null;
        
        // Initialize frontend key pair
        this.initializeFrontendKeyPair();
    }

    /**
     * Initialize frontend's own RSA key pair for receiving encrypted responses
     */
    async initializeFrontendKeyPair() {
        try {
            if (!this.isSupported) {
                return;
            }

            // console.log('🔐 Generating frontend RSA key pair for bidirectional encryption...');
            
            // Generate RSA key pair
            this.frontendKeyPair = await crypto.subtle.generateKey(
                {
                    name: this.rsaAlgorithm,
                    modulusLength: 2048,
                    publicExponent: new Uint8Array([1, 0, 1]),
                    hash: 'SHA-256'
                },
                true, // extractable
                ['encrypt', 'decrypt']
            );

            // Export public key to PEM format for sending to backend
            const exportedPublicKey = await crypto.subtle.exportKey('spki', this.frontendKeyPair.publicKey);
            this.frontendPublicKeyPem = this.arrayBufferToPem(exportedPublicKey, 'PUBLIC KEY');
            
            console.log('✅ Frontend key pair generated successfully');
            // console.log('🔑 Frontend public key preview:', this.frontendPublicKeyPem.substring(0, 100) + '...');
            
        } catch (error) {
            console.error('❌ Failed to generate frontend key pair:', error);
            this.frontendKeyPair = null;
            this.frontendPublicKeyPem = null;
        }
    }

    /**
     * Convert ArrayBuffer to PEM format
     * @param {ArrayBuffer} keyBuffer - Key in ArrayBuffer format
     * @param {string} keyType - Type of key ('PUBLIC KEY' or 'PRIVATE KEY')
     * @returns {string} - PEM formatted key
     */
    arrayBufferToPem(keyBuffer, keyType) {
        const keyBase64 = this.arrayBufferToBase64(keyBuffer);
        const keyFormatted = keyBase64.match(/.{1,64}/g).join('\n');
        return `-----BEGIN ${keyType}-----\n${keyFormatted}\n-----END ${keyType}-----`;
    }

    /**
     * Check if encryption is supported in current browser
     * @returns {boolean} - True if Web Crypto API is available
     */
    isEncryptionSupported() {
        return this.isSupported;
    }

    /**
     * Fetch backend's public key from server
     * @returns {Promise<boolean>} - True if successful
     */
    async fetchBackendPublicKey() {
        try {
            if (this.publicKeyFetched && this.backendPublicKey) {
                console.log('✅ Using cached backend public key');
                return true;
            }

            const baseUrl = process.env.REACT_APP_API_BASE_URL_ADMIN;
            const url = `${baseUrl}/encryption/public-key`;
            console.log('🔑 Fetching backend public key from:', url);
            console.log('🌐 Base URL from env:', baseUrl);
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            console.log('📡 Backend public key response status:', response.status);
            console.log('📡 Backend public key response ok:', response.ok);

            if (!response.ok) {
                console.error('❌ Backend public key fetch failed with status:', response.status);
                console.error('❌ Response status text:', response.statusText);
                throw new Error(`Failed to fetch backend public key: ${response.status} - ${response.statusText}`);
            }

            const data = await response.json();
            console.log('📋 Backend public key response data:', data);
            
            if (data.status && data.data.publicKey) {
                this.backendPublicKey = data.data.publicKey;
                this.publicKeyFetched = true;
                console.log('✅ Backend public key fetched and cached successfully');
                console.log('🔑 Backend public key preview:', this.backendPublicKey.substring(0, 50) + '...');
                return true;
            } else {
                console.error('❌ Invalid response format for backend public key:', data);
                throw new Error('Invalid response format for backend public key');
            }
        } catch (error) {
            console.error('❌ Error fetching backend public key:', error);
            console.error('❌ Error details:', {
                message: error.message,
                stack: error.stack,
                name: error.name
            });
            this.publicKeyFetched = false;
            return false;
        }
    }

    /**
     * Convert PEM string to ArrayBuffer
     * @param {string} pem - PEM formatted key
     * @returns {ArrayBuffer} - ArrayBuffer representation
     */
    pemToArrayBuffer(pem) {
        const b64Lines = pem.replace(/-----[^-]+-----/g, '').replace(/\s/g, '');
        const binaryString = atob(b64Lines);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }

    /**
     * Convert ArrayBuffer to base64 string
     * @param {ArrayBuffer} buffer - ArrayBuffer to convert
     * @returns {string} - Base64 string
     */
    arrayBufferToBase64(buffer) {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }

    /**
     * Generate random AES key
     * @returns {Promise<{key: CryptoKey, keyBuffer: Uint8Array}>} - AES key and buffer
     */
    async generateAESKey() {
        const keyBuffer = crypto.getRandomValues(new Uint8Array(32)); // 256-bit key
        const key = await crypto.subtle.importKey(
            'raw',
            keyBuffer,
            { name: this.algorithm },
            false,
            ['encrypt', 'decrypt']
        );
        return { key, keyBuffer };
    }

    /**
     * Import backend's RSA public key for encryption
     * @returns {Promise<CryptoKey>} - Imported public key
     */
    async importBackendRSAPublicKey() {
        if (!this.backendPublicKey) {
            throw new Error('Backend public key not available. Call fetchBackendPublicKey() first.');
        }

        const keyBuffer = this.pemToArrayBuffer(this.backendPublicKey);
        return await crypto.subtle.importKey(
            'spki',
            keyBuffer,
            {
                name: this.rsaAlgorithm,
                hash: 'SHA-256'
            },
            false,
            ['encrypt']
        );
    }

    /**
     * Encrypt payload using hybrid encryption (RSA + AES) with bidirectional key exchange
     * @param {object} payload - Data to encrypt
     * @returns {Promise<object>} - Hybrid encrypted payload with frontend public key
     */
    async encryptHybridPayload(payload) {
        if (!this.isSupported) {
            throw new Error('Encryption not supported in this browser');
        }

        try {
            // Ensure we have the backend public key
            if (!this.publicKeyFetched) {
                const success = await this.fetchBackendPublicKey();
                if (!success) {
                    throw new Error('Failed to fetch backend public key');
                }
            }

            // Ensure we have our own key pair
            if (!this.frontendKeyPair) {
                await this.initializeFrontendKeyPair();
                if (!this.frontendKeyPair) {
                    throw new Error('Failed to generate frontend key pair');
                }
            }

            // Step 1: Generate random AES key
            const { key: aesKey, keyBuffer: aesKeyBuffer } = await this.generateAESKey();
            const iv = crypto.getRandomValues(new Uint8Array(16));

            // Step 2: Encrypt data with AES
            const jsonString = JSON.stringify(payload);
            const encodedData = new TextEncoder().encode(jsonString);
            
            const encryptedData = await crypto.subtle.encrypt(
                {
                    name: this.algorithm,
                    iv: iv
                },
                aesKey,
                encodedData
            );

            // Step 3: Encrypt AES key with backend's RSA public key
            const backendRSAPublicKey = await this.importBackendRSAPublicKey();
            const encryptedAESKey = await crypto.subtle.encrypt(
                {
                    name: this.rsaAlgorithm
                },
                backendRSAPublicKey,
                aesKeyBuffer
            );

            // Step 4: Create hash for integrity
            const hash = await this.createHash(jsonString);

            return {
                hybridEncrypted: true,
                encryptedAESKey: this.arrayBufferToBase64(encryptedAESKey),
                encryptedData: this.arrayBufferToBase64(encryptedData),
                iv: this.arrayBufferToBase64(iv.buffer),
                hash: hash,
                algorithm: 'RSA-OAEP-SHA256-AES-256-CBC',
                // Include frontend's public key for response encryption
                frontendPublicKey: this.frontendPublicKeyPem
            };
        } catch (error) {
            console.error('Hybrid encryption error:', error);
            throw new Error('Failed to encrypt payload');
        }
    }

    /**
     * Creates a secure hash for integrity checking using Web Crypto API
     * @param {string} data - Data to hash
     * @returns {Promise<string>} - Hash string
     */
    async createHash(data) {
        const encoder = new TextEncoder();
        const dataBuffer = encoder.encode(data);
        const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
        
        // Convert ArrayBuffer to hex string
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
        
        return hashHex;
    }

    /**
     * Decrypt hybrid encrypted response from backend (handles both frontend and backend key encryption)
     * @param {object} encryptedResponse - Hybrid encrypted response
     * @returns {Promise<object>} - Decrypted response data
     */
    async decryptHybridResponse(encryptedResponse) {
        try {
            const { encryptedAESKey, encryptedData, iv, hash, algorithm } = encryptedResponse;

            // Convert base64 to ArrayBuffer
            const encryptedAESKeyBuffer = this.base64ToArrayBuffer(encryptedAESKey);
            const encryptedDataBuffer = this.base64ToArrayBuffer(encryptedData);
            const ivBuffer = this.base64ToArrayBuffer(iv);

            let aesKeyBuffer;

            // Check if this was encrypted with backend key (fallback scenario)
            if (algorithm && algorithm.includes('BACKEND-KEY')) {
                console.log('⚠️ Response was encrypted with backend key - attempting shared key decryption');
                
                // For responses encrypted with backend key, we need to use a shared approach
                // This is a limitation of the current fallback implementation
                // In this case, we'll try to derive a shared key or use a simpler approach
                
                // For now, let's indicate this needs special handling
                console.warn('Backend key encrypted responses require special handling');
                throw new Error('Backend key encrypted responses not fully supported yet - this needs shared key derivation');
            }

            // Normal case: response encrypted with frontend's public key
            if (!this.frontendKeyPair) {
                throw new Error('Frontend key pair not available for response decryption');
            }

            // Step 1: Decrypt AES key using frontend's private key
            aesKeyBuffer = await crypto.subtle.decrypt(
                {
                    name: this.rsaAlgorithm
                },
                this.frontendKeyPair.privateKey,
                encryptedAESKeyBuffer
            );

            // Step 2: Import the decrypted AES key
            const aesKey = await crypto.subtle.importKey(
                'raw',
                aesKeyBuffer,
                { name: this.algorithm },
                false,
                ['decrypt']
            );

            // Step 3: Decrypt data using AES key
            const decryptedDataBuffer = await crypto.subtle.decrypt(
                {
                    name: this.algorithm,
                    iv: ivBuffer
                },
                aesKey,
                encryptedDataBuffer
            );

            // Step 4: Convert back to string and parse JSON
            const decryptedString = new TextDecoder().decode(decryptedDataBuffer);
            const decryptedData = JSON.parse(decryptedString);

            // Step 5: Verify hash integrity if provided
            if (hash) {
                const computedHash = await this.createHash(decryptedString);
                if (computedHash !== hash) {
                    console.warn('⚠️ Response hash verification failed');
                    throw new Error('Response integrity check failed');
                }
            }

            return decryptedData;
            
        } catch (error) {
            console.error('❌ Hybrid response decryption error:', error);
            
            // If the response has a warning about backend key encryption, return the unencrypted data
            if (encryptedResponse.warning && encryptedResponse.data) {
                console.log('⚠️ Returning fallback unencrypted data due to backend key encryption');
                return encryptedResponse.data;
            }
            
            throw new Error('Failed to decrypt hybrid response');
        }
    }

    /**
     * Convert base64 to ArrayBuffer
     * @param {string} base64 - Base64 string
     * @returns {ArrayBuffer} - ArrayBuffer representation
     */
    base64ToArrayBuffer(base64) {
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }

    /**
     * Prepare encrypted payload for API request
     * @param {object} payload - Original payload to encrypt
     * @returns {Promise<object>} - Encrypted payload
     */
    async prepareEncryptedPayload(payload) {
        try {
            console.log('🔐 Starting hybrid encryption for payload...');
            console.log('📦 Payload to encrypt:', payload);
            
            // Check if encryption is supported
            if (!this.isSupported) {
                console.error('❌ Web Crypto API not supported in this browser');
                throw new Error('Encryption not supported in this browser');
            }

            // Encrypt using hybrid method
            console.log('🔄 Attempting hybrid encryption...');
            const encryptedPayload = await this.encryptHybridPayload(payload);
            console.log('✅ Hybrid encryption successful');
            console.log('📋 Encrypted payload structure:', {
                hybridEncrypted: encryptedPayload.hybridEncrypted,
                hasEncryptedAESKey: !!encryptedPayload.encryptedAESKey,
                hasEncryptedData: !!encryptedPayload.encryptedData,
                hasIV: !!encryptedPayload.iv,
                hasHash: !!encryptedPayload.hash,
                algorithm: encryptedPayload.algorithm
            });
            
            return encryptedPayload;
            
        } catch (error) {
            console.error('❌ Hybrid encryption failed:', error);
            console.error('❌ Error details:', {
                message: error.message,
                stack: error.stack,
                name: error.name
            });
            throw new Error(`Encryption failed: ${error.message}`);
        }
    }

    /**
     * Decrypt API response (handles both hybrid and legacy symmetric responses)
     * @param {object} response - Encrypted response from API
     * @returns {Promise<object>} - Decrypted response data
     */
    async decryptResponse(response) {
        try {
            console.log('🔍 Decrypting response:', { 
                encrypted: response.encrypted, 
                hybridEncrypted: response.hybridEncrypted,
                hasData: !!response.data,
                keys: Object.keys(response)
            });

            // Handle non-encrypted responses
            if (!response.encrypted && !response.hybridEncrypted) {
                console.log('✅ Response is not encrypted, returning as-is');
                return response;
            }

            // If encryption is not supported, return response as-is with warning
            if (!this.isSupported) {
                console.warn('⚠️ Encryption not supported, cannot decrypt response');
                return response;
            }

            // Handle hybrid encrypted responses (new bidirectional encryption)
            if (response.hybridEncrypted && response.encryptedAESKey) {
                console.log('🔐 Decrypting hybrid encrypted response...');
                try {
                    const decryptedData = await this.decryptHybridResponse(response);
                    console.log('✅ Hybrid response decryption successful');
                    return decryptedData;
                } catch (hybridError) {
                    console.error('❌ Hybrid response decryption failed:', hybridError);
                    // Don't fallback for hybrid - if it fails, it's a real error
                    throw hybridError;
                }
            }

            // Handle legacy symmetric encrypted responses
            if (response.encrypted && !response.hybridEncrypted) {
                console.log('🔐 Decrypting legacy symmetric response...');
                try {
                    const { encryptedData, iv, hash } = response;
                    
                    // Use legacy symmetric decryption for responses
                    const legacyUtils = await import('./encryptionUtils.js');
                    const decryptedData = await legacyUtils.default.decryptJson({ encryptedData, iv });

                    // Verify hash if provided
                    if (hash && !(await legacyUtils.default.verifyHash(JSON.stringify(decryptedData), hash))) {
                        console.warn('⚠️ Response hash verification failed');
                    }

                    console.log('✅ Legacy response decryption successful');
                    return decryptedData;
                } catch (legacyError) {
                    console.error('❌ Legacy response decryption failed:', legacyError);
                    console.warn('⚠️ Returning response as-is due to decryption error');
                    return response;
                }
            }

            console.log('✅ Response processing complete (no encryption detected)');
            return response;
        } catch (error) {
            console.error('❌ Error decrypting response:', error);
            console.warn('⚠️ Returning response as-is due to decryption error');
            return response;
        }
    }

    /**
     * Get encryption status and info
     * @returns {object} - Status information
     */
    getStatus() {
        return {
            isSupported: this.isSupported,
            publicKeyFetched: this.publicKeyFetched,
            encryptionType: 'hybrid-rsa-aes',
            algorithms: {
                keyExchange: 'RSA-OAEP-2048',
                dataEncryption: 'AES-256-CBC',
                hashing: 'SHA-256'
            }
        };
    }
}

export default new AsymmetricEncryptionUtils();
